import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  Badge,
  IconButton,
  VStack,
  HStack,
  Icon,
  Center,
  Button,
} from "@chakra-ui/react";
import { Tooltip } from "../../../ui/tooltip";

import { useNotificationContext } from "../../../../Contexts/NotificationContext";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import useThemeColors from "../../../../hooks/useThemeColors";
import { Notification, User } from "../../../../services/Dtos";
import { NotificationType } from "../../../../services/Enums";
import useIcons from "../../../../hooks/useIcons";

interface NotificationItemProps {
  notification: Notification;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
}) => {
  const { markAsRead, deleteNotification, getNotificationTypeLabel } =
    useNotificationContext();
  const { users } = useSchoolsContext(); // Use users from SchoolContext instead
  const colors = useThemeColors();
  const [isExpanded, setIsExpanded] = useState(false);
  const [sender, setSender] = useState<User | null>(null);
  const [senderLoading, setSenderLoading] = useState(false);
  const hasFetchedSender = useRef(false);
  const icons = useIcons();

  // Get sender from already-loaded users instead of making API call
  useEffect(() => {
    const getSenderFromCache = () => {
      // For system notifications, don't fetch sender
      if (notification.type === NotificationType.SystemMessage) {
        return;
      }

      // For other notification types, find sender in already-loaded users
      if (notification.senderId && !hasFetchedSender.current) {
        hasFetchedSender.current = true;
        setSenderLoading(true);

        // Find the sender in the already-loaded users array
        const senderUser = users.find(
          (user) => user.id === notification.senderId
        );
        setSender(senderUser || null);
        setSenderLoading(false);
      }
    };

    getSenderFromCache();
  }, [
    notification.id,
    users, // Depend on users array from SchoolContext
  ]);

  const handleMarkAsRead = async () => {
    await markAsRead(notification.id);
  };

  const handleDelete = async () => {
    await deleteNotification(notification.id);
  };

  const getSenderDisplay = () => {
    if (notification.type === NotificationType.SystemMessage) {
      return "Result.ng";
    }

    if (senderLoading) {
      return "Loading...";
    }

    if (sender) {
      return `${sender.firstName} ${sender.lastName} (${sender.email})`;
    }

    // Fallback to "Unknown User" instead of showing the cryptic ID
    return "Unknown User";
  };

  const toggleExpand = () => {
    if (notification.message.length > 100) {
      setIsExpanded(!isExpanded);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  };

  const IconComponent = getNotificationTypeLabel(notification.type).icon;
  const colorPalette = getNotificationTypeLabel(notification.type).color;

  return (
    <Box
      p={3}
      borderRadius="md"
      my={1}
      border="1px solid"
      borderColor={colors.border.primary}
      bg={
        notification.isRead
          ? colors.background.primary
          : colors.background.secondary
      }
      _hover={{ bg: colorPalette + "/5" }}
      transition="background-color 0.2s"
      position="relative"
      borderLeft={!notification.isRead ? "4px solid" : "1px solid"}
      borderLeftColor={
        !notification.isRead ? `${colorPalette}.500` : colors.border.primary
      }
      onClick={toggleExpand}
      cursor="pointer"
    >
      <HStack gap={3} align="flex-start">
        {/* Icon */}
        <Center
          borderRadius="md"
          bg={`${colorPalette}.100`}
          color={`${colorPalette}.600`}
          flexShrink={0}
          aspectRatio={1}
          boxSize={7}
        >
          <Icon as={IconComponent} boxSize={4} />
        </Center>

        {/* Content */}
        <VStack gap={1} align="flex-start" flex={1} minW={0}>
          <HStack gap={2} w="full" justify="space-between">
            <Text
              fontSize="sm"
              fontWeight={notification.isRead ? "normal" : "semibold"}
              color={colors.text.primary}
              lineClamp={1}
            >
              {notification.title}
            </Text>
            <Badge
              size="sm"
              colorPalette={colorPalette}
              variant="subtle"
              fontSize="xs"
            >
              {getNotificationTypeLabel(notification.type).label}
            </Badge>
          </HStack>

          {/* Sender information */}
          <Text fontSize="xs" color={colors.text.tertiary} fontWeight="medium">
            From: {getSenderDisplay()}
          </Text>

          <VStack gap={1} align="flex-start" w="full">
            <Text
              fontSize="xs"
              color={colors.text.secondary}
              lineClamp={isExpanded ? undefined : 2}
              lineHeight="1.4"
            >
              {notification.message}
            </Text>

            {/* Show expand/collapse button if message is long */}
            {notification.message.length > 100 && (
              <Tooltip
                content={isExpanded ? "Show less" : "Show more"}
                positioning={{ placement: "top" }}
                asChild
              >
                <Button
                  aria-label={isExpanded ? "Show less" : "Show more"}
                  size="xs"
                  variant="ghost"
                  onClick={() => setIsExpanded(!isExpanded)}
                  colorPalette="gray"
                  h="auto"
                  minH="20px"
                  px={1}
                >
                  {isExpanded ? "show less" : "show more"}
                  {isExpanded ? (
                    <icons.utility.chevronUp />
                  ) : (
                    <icons.utility.chevronDown />
                  )}
                </Button>
              </Tooltip>
            )}
          </VStack>

          <HStack gap={2} w="full" justify="space-between">
            <HStack gap={1} color={colors.text.tertiary}>
              <Icon as={icons.utility.clock} boxSize={3} />
              <Text fontSize="xs">{formatTimeAgo(notification.createdAt)}</Text>
            </HStack>

            {/* Actions */}
            <HStack gap={1}>
              {!notification.isRead && (
                <Tooltip
                  content="Mark as read"
                  positioning={{ placement: "top" }}
                  asChild
                >
                  <IconButton
                    aria-label="Mark as read"
                    size="xs"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead();
                    }}
                    colorPalette="green"
                  >
                    <icons.utility.check />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip content="Delete" positioning={{ placement: "top" }}>
                <IconButton
                  aria-label="Delete notification"
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  colorPalette="red"
                >
                  <icons.utility.delete />
                </IconButton>
              </Tooltip>
            </HStack>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
};

export default NotificationItem;
